import { useContext, useState } from 'react';
import { Subtitle1 } from '@fluentui/react-components';

import {
  StatusLabel,
  Square,
  SquareButton,
  SingleFieldDialog,
  TSingleFieldDialogState,
  ConfirmDialog,
  TConfirmDialogState,
} from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';
import ActivitySection from '@shared/pages/Home/ActivitySection';
import { TERMINOLOGY } from '@shared/i18n';

import { SignInContext } from '@modules/users';
import { AccessDialog, TAccessDialogState, AccessType } from '@modules/access';

import { useGeneralActions } from '../hooks/useActions.hook';
import { useCreate, useFolder } from '../api/folders.api';
import { IFolder, isTFolder } from '../types/folders.types';

import FoldersNavDrawer from '../components/nav/FoldersNavDrawer';

type TPropsContent = {
  folder: IFolder;
};

const CountriesContent = ({ folder }: TPropsContent) => {
  const [simpleDialogState, setSimpleDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(undefined);
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const actions = useGeneralActions({
    setSimpleDialogState,
    setConfirmDialogState,
    setAccessDialogState,
    folder,
  });

  const create = useCreate();
  const handleCreate = () => {
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText: '',
      title: `New ${TERMINOLOGY.registrationCountry.lower}`,
      label: 'Name',
      buttonLabel: 'Create',
      onSubmit: (formData) => {
        create.mutate(
          { parentId: folder._id, name: formData.text },
          { onSuccess: () => setSimpleDialogState({ ...dialogState, isOpen: false }) }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };

  const fullPath = [...folder.resolvedPath, { _id: folder._id, name: folder.name, accessRight: folder.accessRight }];

  return (
    <div className="flex flex-1">
      <FoldersNavDrawer fullPath={fullPath} selectedValue="registrations" folder={folder} />
      <div className="grow h-full w-full relative">
        <div
          className="absolute top-0 left-0 w-full h-full overflow-y-auto pl-1 pr-8"
          style={{ scrollbarGutter: 'stable' }}
        >
          <div className="pt-4 pb-16">
            <div className="flex flex-col">
              <SingleFieldDialog state={simpleDialogState} setState={setSimpleDialogState} />
              <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
              <AccessDialog state={accessDialogState} setState={setAccessDialogState} />

              <p className="flex space-x-4 mb-5">
                <Subtitle1>{TERMINOLOGY.registrationFull.upper}</Subtitle1>
              </p>
              <div className="flex flex-wrap gap-5 pb-12">
                {folder.children.map((item) => (
                  <Square key={item._id} item={item} isRoot={false} actions={actions} />
                ))}
                {folder.accessRight >= AccessType.EDIT && (
                  <SquareButton handleCreate={handleCreate}>
                    {`New ${TERMINOLOGY.registrationCountry.lower}`}
                  </SquareButton>
                )}
              </div>

              <ActivitySection recentItems={folder.recentItems} pendingApprovals={folder.pendingApprovals} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Countries = () => {
  const { organization } = useContext(SignInContext);
  const countriesFolderId = organization.folders.countries;
  const { error, data } = useFolder(countriesFolderId, { enabled: Boolean(countriesFolderId) });

  useDocumentTitle(data && isTFolder(data) ? data.name : TERMINOLOGY.registrationFull.upper);

  if (!countriesFolderId)
    return (
      <p className="flex justify-center p-4 text-lg">{`${TERMINOLOGY.registrationFull.upper} are not configured.`}</p>
    );

  return (
    <StatusLabel
      data={data}
      loadingMessage={`Loading ${TERMINOLOGY.registrationFull.lower}...`}
      error={error}
      errorMessage={`Loading ${TERMINOLOGY.registrationFull.lower} failed!`}
      render={(resolvedData) => isTFolder(resolvedData) && <CountriesContent folder={resolvedData} />}
    />
  );
};

export default Countries;
